/**
 * AI refinement affordances for schema review: sparkle icon, inline refine
 * panel, skeleton loading, and apply/discard preview.
 */
import React, { useState } from "react"

export function SparkleIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" />
      <path d="M19 15 L19.6 17.4 L22 18 L19.6 18.6 L19 21 L18.4 18.6 L16 18 L18.4 17.4 Z" opacity="0.7" />
    </svg>
  )
}

export function SchemaRefineSkeleton({ rows = 3 }) {
  return (
    <div className="space-y-2 animate-pulse" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-3 rounded bg-amber-100/80" style={{ width: `${70 + (i % 3) * 10}%` }} />
      ))}
      <div className="h-8 rounded bg-amber-100/60" />
    </div>
  )
}

const FIELD_SUGGESTIONS = [
  "Improve the description",
  "Suggest a clearer field name",
  "Fix type and constraints",
]

const SCHEMA_SUGGESTIONS = [
  "Address lint warnings",
  "Make all descriptions clearer",
  "Review required vs optional fields",
]

export function SchemaAiRefineButton({
  disabled = false,
  busy = false,
  label = "Refine with AI",
  title = "Get AI suggestions for this field",
  onClick,
  compact = false,
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40 ${
        compact ? "px-2 py-1 text-xs" : "px-2.5 py-1.5 text-sm"
      }`}
    >
      {busy ? (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-amber-300 border-t-amber-700" />
      ) : (
        <SparkleIcon className="h-3.5 w-3.5" />
      )}
      {!compact && <span>{label}</span>}
    </button>
  )
}

export function SchemaAiRefinePanel({
  open,
  scope = "field",
  loading = false,
  result = null,
  error = null,
  onSubmit,
  onApply,
  onDiscard,
  onClose,
}) {
  const [instruction, setInstruction] = useState("")
  const suggestions = scope === "schema" ? SCHEMA_SUGGESTIONS : FIELD_SUGGESTIONS

  if (!open) return null

  return (
    <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-800">
          AI refine {scope === "schema" ? "schema" : "field"}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-violet-600 hover:text-violet-900"
        >
          Close
        </button>
      </div>

      {!result && !loading && (
        <>
          <div className="mb-2 flex flex-wrap gap-1">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setInstruction(s)}
                className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[11px] text-violet-800 hover:bg-violet-100"
              >
                {s}
              </button>
            ))}
          </div>
          <textarea
            rows={2}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Describe what to improve…"
            className="w-full rounded-md border border-violet-200 bg-white px-2 py-1.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              disabled={!instruction.trim() || loading}
              onClick={() => onSubmit?.(instruction.trim())}
              className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Refine
            </button>
          </div>
        </>
      )}

      {loading && (
        <div role="status" aria-live="polite" className="py-1">
          <p className="mb-2 text-xs text-violet-700">AI is refining…</p>
          <SchemaRefineSkeleton rows={scope === "schema" ? 5 : 3} />
        </div>
      )}

      {error && !loading && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      {result && !loading && (
        <div className="space-y-2">
          {result.explanation ? (
            <p className="text-xs leading-snug text-violet-900">{result.explanation}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onDiscard}
              className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={onApply}
              className="rounded-md bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
